(() => {
  'use strict';

  const GRID_SELECTOR = '#product-grid';
  const SENTINEL_SELECTOR = '#StahkInfiniteScroll';
  const PAGINATION_SELECTOR = '.pagination-wrapper';

  let intersectionObserver = null;
  let mutationTimer = null;

  function getSentinel() {
    return document.querySelector(SENTINEL_SELECTOR);
  }

  function getGrid() {
    return document.querySelector(GRID_SELECTOR);
  }

  function showLoading(sentinel, isLoading) {
    const loader = sentinel.querySelector(
      '.stahk-infinite-scroll__loader'
    );

    if (loader) {
      loader.hidden = !isLoading;
    }

    sentinel.dataset.loading = isLoading ? 'true' : 'false';
  }

  function showComplete(sentinel) {
    const loader = sentinel.querySelector(
      '.stahk-infinite-scroll__loader'
    );

    const completeMessage = sentinel.querySelector(
      '.stahk-infinite-scroll__complete'
    );

    if (loader) {
      loader.hidden = true;
    }

    if (completeMessage) {
      completeMessage.hidden = false;
    }

    sentinel.dataset.nextUrl = '';
    sentinel.dataset.loading = 'false';
  }

  async function loadNextPage(sentinel) {
    const nextUrl = sentinel.dataset.nextUrl;

    if (
      !nextUrl ||
      sentinel.dataset.loading === 'true'
    ) {
      return;
    }

    const currentGrid = getGrid();

    if (!currentGrid) {
      return;
    }

    showLoading(sentinel, true);

    try {
      const response = await fetch(nextUrl, {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(
          `Unable to load products: ${response.status}`
        );
      }

      const html = await response.text();
      const nextDocument = new DOMParser().parseFromString(
        html,
        'text/html'
      );

      const nextGrid = nextDocument.querySelector(GRID_SELECTOR);
      const nextSentinel =
        nextDocument.querySelector(SENTINEL_SELECTOR);

      if (!nextGrid) {
        throw new Error('Next product grid was not found.');
      }

      const newProducts = Array.from(nextGrid.children);

      newProducts.forEach((product) => {
        const importedProduct = document.importNode(product, true);
        importedProduct.classList.add(
          'stahk-infinite-scroll__new-product'
        );

        currentGrid.appendChild(importedProduct);
      });

      const followingUrl =
        nextSentinel?.dataset.nextUrl || '';

      sentinel.dataset.nextUrl = followingUrl;
      showLoading(sentinel, false);

      document.dispatchEvent(
        new CustomEvent('stahk:products-loaded', {
          detail: {
            container: currentGrid,
            products: newProducts.length
          }
        })
      );

      if (!followingUrl) {
        showComplete(sentinel);

        if (intersectionObserver) {
          intersectionObserver.disconnect();
        }
      }
    } catch (error) {
      console.error('STAHK infinite scroll error:', error);

      showLoading(sentinel, false);

      /*
       * Restore normal pagination if loading fails.
       * This prevents customers from becoming stuck.
       */
      document.documentElement.classList.remove(
        'stahk-infinite-scroll-enabled'
      );
    }
  }

  function initializeInfiniteScroll() {
    const sentinel = getSentinel();
    const grid = getGrid();

    if (!sentinel || !grid) {
      return;
    }

    if (intersectionObserver) {
      intersectionObserver.disconnect();
    }

    document.documentElement.classList.add(
      'stahk-infinite-scroll-enabled'
    );

    const nextUrl = sentinel.dataset.nextUrl;

    if (!nextUrl) {
      showComplete(sentinel);
      return;
    }

    intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (entry?.isIntersecting) {
          loadNextPage(sentinel);
        }
      },
      {
        /*
         * Begin loading before the customer reaches the end.
         */
        root: null,
        rootMargin: '600px 0px',
        threshold: 0
      }
    );

    intersectionObserver.observe(sentinel);
  }

  function observeCollectionUpdates() {
    const collectionContainer =
      document.querySelector('#ProductGridContainer');

    if (!collectionContainer) {
      return;
    }

    const mutationObserver = new MutationObserver(() => {
      clearTimeout(mutationTimer);

      mutationTimer = window.setTimeout(() => {
        initializeInfiniteScroll();
      }, 100);
    });

    mutationObserver.observe(collectionContainer, {
      childList: true,
      subtree: true
    });
  }

  function initialize() {
    initializeInfiniteScroll();
    observeCollectionUpdates();

    document.addEventListener(
      'shopify:section:load',
      initializeInfiniteScroll
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }
})();